import { SolutionPage } from "@/components/shared/SolutionPage";
import { VoiceAIMockup } from "@/components/shared/VoiceAIMockup";
import { Phone, Calendar, Network, TrendingUp, Check } from 'lucide-react';
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";
import { MissedCallCalculator } from "@/components/shared/MissedCallCalculator";

/**
 * Frayze Reception - the named, publicly-priced managed AI receptionist product
 * (entry SKU, $500 build + $197/mo). Upgraded from the "AI Receptionist" capability
 * spoke 2026-07-18 as a bounded entry-SKU experiment (keep/kill at the 2026-09-14
 * offer review - projects/frayze-growth/DASHBOARD.md in Denisbot). Same URL kept
 * for SEO equity. Single-intent for "ai receptionist" / "managed ai receptionist"
 * demand. Outcome-framed, no invented metrics, no competitor names on-page.
 */
const AIReceptionist = () => {
  // React Router doesn't auto-scroll to hashes; honour #reception-pricing links
  // both on landing and when clicked from the hero on this same page.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#reception-pricing") {
      document.getElementById("reception-pricing")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  return (
    <SolutionPage
      breadcrumbTitle="Frayze Reception"
      seo={{
        title: "Frayze Reception: Managed AI Receptionist from $197/mo | Frayze",
        description: "Frayze Reception is a managed AI receptionist. We build it, train it on your business, and wire it into a CRM. Every call answered and booked 24/7, from $197/month, no contracts.",
        keywords: "frayze reception, managed ai receptionist, ai receptionist, ai receptionist pricing, ai phone answering, voice ai, ai front desk, virtual receptionist, ai call answering, automated phone answering, ai appointment booking, ai receptionist canada",
        canonical: "https://frayze.ca/platform/ai-receptionist/",
        structuredData: {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Frayze Reception",
          "serviceType": "Managed AI receptionist",
          "provider": { "@type": "Organization", "name": "Frayze Technologies Inc.", "url": "https://frayze.ca" },
          "areaServed": "CA",
          "description": "A managed AI phone receptionist that answers every call 24/7, books appointments, and logs every caller into a CRM. Built, trained, and tuned by Frayze.",
          "offers": {
            "@type": "Offer",
            "price": "197",
            "priceCurrency": "CAD",
            "description": "$197/month managed service plus one-time $500 Required Build & Onboarding. No contracts."
          }
        }
      }}
      hero={{
        badge: "Frayze Reception · Managed AI Voice",
        badgeColor: "text-purple-700",
        title: "Every Call Answered. Day or Night.",
        description: "Frayze Reception is a managed AI receptionist. It picks up on the first ring, answers in your business's voice, books the appointment, and logs the caller in a real CRM. We build it, train it, and run it for you. From $197/month, no contracts.",
        buttonColor: "bg-purple-700 hover:bg-purple-800",
        gradientParams: "from-purple-500/5 via-background to-background",
        primaryCta: { text: "Hear It in Action", link: "/book-consultation/?tier=intelligence" },
        secondaryCta: { text: "See What's Included", link: "#reception-pricing" },
        visual: <VoiceAIMockup />
      }}
      problem={{
        title: "You Can't Answer Every Call. It's Costing You.",
        description: (
          <>
            After hours, on the other line, slammed on a Saturday. The calls you miss are leads walking to a competitor who picked up. Voicemail doesn't save them.
            <br /><br />
            <strong>The industry research is brutal:</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "62%", label: "Of calls to local businesses go unanswered at peak times" },
          { value: "85%", label: "Of callers sent to voicemail won't leave a message or call back" },
          { value: "24/7", label: "When leads actually call: nights, weekends, and holidays included" }
        ]
      }}
      systemModel={{
        title: "How the AI Receptionist Works",
        description: "It answers like a trained front-desk employee, because you train it like one.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-purple-500/20 via-purple-500/50 to-purple-500/20",
        items: [
          { step: "1", title: "It Answers", description: "Every call, on the first ring, in a natural voice with your business name and tone." },
          { step: "2", title: "It Understands", description: "Callers ask real questions about hours, pricing, and availability, and get real answers from your knowledge base." },
          { step: "3", title: "It Books & Captures", description: "It books appointments on your calendar and logs every caller as a lead in your CRM." },
          { step: "4", title: "It Hands Off", description: "Complex or high-value calls get routed or transferred to a human, with the context attached." }
        ]
      }}
      layers={{
        title: "What the AI Receptionist Handles",
        description: "A front-desk employee that never sleeps. Built, trained, and managed for you.",
        theme: "purple",
        items: [
          {
            title: "On the Phone",
            icon: Phone,
            items: [
              "Answers 24/7 on the first ring",
              "Natural voice in your brand's tone",
              "Answers FAQs from your knowledge base"
            ]
          },
          {
            title: "Behind the Scenes",
            icon: Calendar,
            items: [
              "Books appointments to your calendar",
              "Logs every caller in the CRM",
              "Escalates or transfers to a human when needed"
            ]
          }
        ]
      }}
      interdependence={{
        title: "The Front Desk for the Whole System",
        description: (
          <>
            The AI receptionist is the voice layer of <Link to="/platform/" className="font-bold underline decoration-purple-500/50 hover:text-purple-600 transition-colors">Frayze's AI capabilities</Link>. It captures every caller into the <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM</Link> and is part of the <Link to="/solutions/ai-strategy-rollout/" className="font-bold underline decoration-purple-500/50 hover:text-purple-600 transition-colors">Enterprise AI layer</Link>.
            <br /><br />
            See where it sits in the wider <Link to="/platform/" className="font-bold underline decoration-purple-500/50 hover:text-purple-600 transition-colors">managed platform</Link>.
          </>
        ),
        icon: Network,
        quote: "Every call it answers feeds the whole machine."
      }}
      useCases={{
        title: "Where an AI Receptionist Earns Its Keep",
        items: [
          { title: "Car Dealerships", description: "Service and sales calls answered and booked 24/7, even when the showroom is closed.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "Emergency and quote calls answered while you're on the tools.", link: "/industries/trades/" },
          { title: "High-Ticket Services", description: "Clinics, firms, and studios that can't afford a single missed booking.", link: "/solutions/ai-strategy-rollout/" }
        ]
      }}
      socialProof={{
        title: "Built by an operator who ran real phone rooms",
        quote: "Before Frayze, Denis ran the lead and phone systems at Need A Car across six rooftops, driving 400% inbound lead growth. The AI receptionist is that same 'never miss a call' discipline, automated.",
        icon: TrendingUp,
        ctaText: "See the Need A Car story",
        ctaLink: "/case-studies/need-a-car-automotive-growth/"
      }}
      cta={{
        title: "Never Send a Caller to Voicemail Again",
        description: "Put an AI receptionist on your line and answer every call, day or night.",
        primaryCta: { text: "Hear It in Action", link: "/book-consultation/?tier=intelligence" },
        secondaryCta: { text: "Explore the Platform", link: "/platform/" },
        color: "bg-purple-700 hover:bg-purple-800"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          <MissedCallCalculator />

          {/* Entry-SKU pricing block . Frayze Reception */}
          <section id="reception-pricing" className="max-w-[1312px] mx-auto px-6 scroll-mt-24">
            <div className="flex flex-col gap-4 max-w-2xl text-left mb-10">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#101319]">
                Simple Pricing. Managed for You.
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#5A6270]">
                Cheap answering apps pick up the phone. Frayze Reception is managed.
                We build it, train it on your business, wire it into a CRM, and keep
                tuning it, so an answered call becomes a booked job instead of a message.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-[#E9E6DE] rounded-2xl p-8 md:p-10 flex flex-col gap-6">
                <div>
                  <div className="text-xs tracking-[0.16em] uppercase text-[#5A6270] font-semibold">One-time</div>
                  <div className="text-4xl font-bold text-[#101319] mt-2">$500</div>
                  <div className="text-sm text-[#5A6270] mt-1">Required Build &amp; Onboarding</div>
                </div>
                <ul className="flex flex-col gap-3">
                  {[
                    "Keep your existing number. No new hardware.",
                    "Voice trained on your business's tone and knowledge",
                    "Calendar and CRM wired in from day one",
                    "Test calls and tuning before it ever answers a real customer",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm md:text-base text-[#101319]">
                      <Check className="w-5 h-5 text-purple-700 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#101319] text-[#F2F3F5] rounded-2xl p-8 md:p-10 flex flex-col gap-6 relative overflow-hidden">
                <div className="absolute top-6 right-6 text-[10px] tracking-[0.14em] uppercase font-bold bg-purple-700 text-white rounded-full px-3 py-1">
                  No contracts
                </div>
                <div>
                  <div className="text-xs tracking-[0.16em] uppercase text-[#9BA3B2] font-semibold">Monthly</div>
                  <div className="text-4xl font-bold text-white mt-2">
                    $197<span className="text-lg font-semibold text-[#9BA3B2]">/mo</span>
                  </div>
                  <div className="text-sm text-[#9BA3B2] mt-1">Managed AI reception. Cancel anytime.</div>
                </div>
                <ul className="flex flex-col gap-3">
                  {[
                    "Every call answered 24/7: nights, weekends, holidays",
                    "Appointments booked, every caller logged as a CRM lead",
                    "Human transfer rules for complex or high-value calls",
                    "Tuning and changes handled for you. Just ask.",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm md:text-base">
                      <Check className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-[#9BA3B2] border-t border-white/10 pt-4">
                  Call minutes run on a small usage credit on top, like a phone plan,
                  scoped to your call volume. Already a Frayze client? Reception plugs
                  straight into your system.
                </div>
                <Link
                  to="/book-consultation/?tier=intelligence"
                  className="text-center bg-purple-700 hover:bg-purple-800 text-white font-bold px-6 py-3.5 rounded-xl shadow-md transition-all hover:scale-[1.02]"
                >
                  Hear It in Action
                </Link>
              </div>
            </div>
          </section>

          <ServiceFAQ
            title="Frayze Reception: Common Questions"
            description="Straight answers on how the voice AI answers, books, and hands off calls."
            items={[
              {
                question: "What is an AI receptionist?",
                answer: "An AI receptionist is a voice assistant that answers your business phone automatically. It picks up on the first ring, speaks in a natural voice, answers common questions, books appointments, and logs the caller. No call goes to voicemail, even after hours."
              },
              {
                question: "What does Frayze Reception cost?",
                answer: "A one-time $500 Required Build & Onboarding fee, then $197/month. No contracts, cancel anytime. Call minutes run on a small usage credit on top, like a phone plan, scoped to your call volume."
              },
              {
                question: "How is this different from a cheap AI answering app?",
                answer: "Self-serve answering tools pick up the phone and send you a message. Frayze Reception is managed. We build and train it, wire it into a calendar and a real CRM, set the human-transfer rules, and keep tuning it as your business changes. The call ends in a booked job, not a notification."
              },
              {
                question: "Does it sound like a robot?",
                answer: "No. It uses a natural voice and is trained on your business's tone and information, so callers get a smooth, on-brand conversation rather than a rigid phone tree."
              },
              {
                question: "Can it book appointments?",
                answer: "Yes. It checks your calendar in real time, books the appointment, and confirms it with the caller. Everything gets logged in your CRM automatically."
              },
              {
                question: "What happens with a call it can't handle?",
                answer: "You set the rules. Complex, sensitive, or high-value calls are transferred or routed to a human, with a summary of what the caller wanted so nothing has to be repeated."
              },
              {
                question: "Does it work after hours and on weekends?",
                answer: "Yes, that's the point. It answers 24/7, including nights, weekends, and holidays, so leads that call outside business hours still get answered and booked."
              },
              {
                question: "Is this the same as missed-call text-back?",
                answer: "They work together. Missed-call text-back replies by SMS when a call isn't picked up; the AI receptionist actually answers the call in voice. Many businesses run both. The AI answers, and text-back covers anything it can't."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default AIReceptionist;
